/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { useMemo, useCallback, useState } from 'react';
import { compareTwoDatesDayEquality } from '../../../utils/dateHelpers.js';
import { useGetStartingFocusedDay } from '../../../utils/hooks/useGetStartingFocusedDay.js';
import type { useCurrentDisplayedMonthYearLogic } from './useCurrentDisplayedMonthYearLogic.js';
import type { DSControlledDateTimePickerT } from '../../../react-desc-prop-types.js';

interface UseFocusLogicArgsT {
  currentDisplayedMonthLogic: ReturnType<typeof useCurrentDisplayedMonthYearLogic>;
  showCalendar: boolean;
}
interface FocusLogicT {
  focusedDay?: DSControlledDateTimePickerT.MetaMonthDay;
  resetFocusedDayToStartDate: () => void;
  handleFocusMetaDay: (metaDayToFocus: DSControlledDateTimePickerT.MetaMonthDay) => void;
  handleFocusMetaDayByDay: (day: Date) => void;
}

export const useFocusLogic = ({ currentDisplayedMonthLogic }: UseFocusLogicArgsT): FocusLogicT => {
  const { daysArraysRef, daysArray } = currentDisplayedMonthLogic;
  const { startFocusedDate } = useGetStartingFocusedDay();
  const [metadayToFocus, setMetadayToFocus] = useState<DSControlledDateTimePickerT.MetaMonthDay | undefined>(undefined);

  // this is required because we have to focus based on the inputs values too,
  // useGetStartingFocusedDay does this already,
  // this "useMemo" is here just to trigger it functionally by changing the metadayToFocus state
  // which is required for arrow key navigation in the calendar
  const focusedDay = useMemo(() => {
    let newFocusedDay;

    if (metadayToFocus)
      newFocusedDay = daysArray.find(({ day: currDay }) => compareTwoDatesDayEquality(currDay, metadayToFocus.day));
    else if (startFocusedDate)
      newFocusedDay = daysArray.find(({ day: currDay }) => compareTwoDatesDayEquality(currDay, startFocusedDate));
    return newFocusedDay;
  }, [daysArray, startFocusedDate, metadayToFocus]);

  const handleFocusMetaDay = useCallback(
    (metaDayToFocus: DSControlledDateTimePickerT.MetaMonthDay) => {
      // if focusedDay is inside current daysArray we set Focused day
      const newFocusedMetaDay = daysArraysRef.current.find(({ day: currDay }) =>
        compareTwoDatesDayEquality(currDay, metaDayToFocus.day),
      );
      if (focusedDay !== newFocusedMetaDay) setMetadayToFocus(newFocusedMetaDay);
    },
    [daysArraysRef, focusedDay],
  );

  const handleFocusMetaDayByDay = useCallback(
    (dayDate: Date) => {
      // if focusedDay is inside current daysArray we set Focused day
      const newFocusedMetaDay = daysArraysRef.current.find(({ day: currDay }) =>
        compareTwoDatesDayEquality(currDay, dayDate),
      );
      if (focusedDay !== newFocusedMetaDay) setMetadayToFocus(newFocusedMetaDay);
    },
    [daysArraysRef, focusedDay],
  );

  const resetFocusedDayToStartDate = useCallback(() => {
    setMetadayToFocus(undefined);
  }, []);

  return useMemo(
    () => ({
      focusedDay,
      resetFocusedDayToStartDate,
      handleFocusMetaDay,
      handleFocusMetaDayByDay,
    }),
    [focusedDay, resetFocusedDayToStartDate, handleFocusMetaDay, handleFocusMetaDayByDay],
  );
};
